import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Box,
  Button,
  Card,
  CardBody,
  DateInput,
  Drop,
  Heading,
  Spinner,
  Text
} from 'grommet'
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query
} from 'firebase/firestore'
import { Bar } from 'react-chartjs-2'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from 'chart.js'
import GlobalConfig from '../GlobalConfig'
import { I18n } from 'galarm-config'
import { FormDown } from 'grommet-icons'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
)

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SHORTCUT_OPTIONS = [
  { labelKey: 'last7Days', days: 7 },
  { labelKey: 'last30Days', days: 30 },
  { labelKey: 'last90Days', days: 90 },
  { labelKey: 'last6Months', days: 180 },
  { labelKey: 'lastYear', days: 365 }
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const todayStr = () => new Date().toISOString().split('T')[0]

const daysAgoStr = n => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d.toISOString().split('T')[0]
}

const formatDuration = ms => {
  if (!ms || ms <= 0) return '—'
  const totalSec = Math.round(ms / 1000)
  const days = Math.floor(totalSec / 86400)
  const hours = Math.floor((totalSec % 86400) / 3600)
  const mins = Math.floor((totalSec % 3600) / 60)
  const secs = totalSec % 60
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${mins}m`
  if (mins > 0) return `${mins}m ${secs}s`
  return `${secs}s`
}

const avg = (sum, count) => (count > 0 ? sum / count : null)

/** Returns the ISO-week Monday date string (YYYY-MM-DD) for a given date string. */
const getWeekStart = dateStr => {
  const d = new Date(dateStr + 'T00:00:00')
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  d.setDate(diff)
  return d.toISOString().split('T')[0]
}

const formatDayLabel = dateStr => {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const formatWeekLabel = weekStartDate => {
  const d = new Date(weekStartDate + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const formatMonthLabel = yearMonth => {
  const [year, month] = yearMonth.split('-')
  const d = new Date(parseInt(year), parseInt(month) - 1, 1)
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

/**
 * Compute daily increments from an ascending-sorted array of cumulative
 * snapshot rows. The first row is treated as context and excluded from output.
 */
const computeIncrements = rows => {
  if (rows.length < 2) return []
  const result = []
  for (let i = 1; i < rows.length; i++) {
    const cur = rows[i]
    const prev = rows[i - 1]
    result.push({
      date: cur.date,
      incidentsOpened: Math.max(
        0,
        (cur.incidentsOpened ?? 0) - (prev.incidentsOpened ?? 0)
      ),
      incidentsClosed: Math.max(
        0,
        (cur.incidentsClosed ?? 0) - (prev.incidentsClosed ?? 0)
      ),
      incidentsAutoClosed: Math.max(
        0,
        (cur.incidentsAutoClosed ?? 0) - (prev.incidentsAutoClosed ?? 0)
      ),
      alarmsSent: Math.max(0, (cur.alarmsSent ?? 0) - (prev.alarmsSent ?? 0)),
      mttaSum: Math.max(0, (cur.mttaSum ?? 0) - (prev.mttaSum ?? 0)),
      mttaCount: Math.max(0, (cur.mttaCount ?? 0) - (prev.mttaCount ?? 0)),
      mttrSum: Math.max(0, (cur.mttrSum ?? 0) - (prev.mttrSum ?? 0)),
      mttrCount: Math.max(0, (cur.mttrCount ?? 0) - (prev.mttrCount ?? 0))
    })
  }
  return result
}

/**
 * Aggregate daily increments by 'daily' | 'weekly' | 'monthly'.
 */
const aggregateIncrements = (increments, aggregation) => {
  if (aggregation === 'daily') {
    return increments.map(r => ({ label: formatDayLabel(r.date), ...r }))
  }

  const groups = new Map()
  for (const row of increments) {
    const key =
      aggregation === 'weekly'
        ? getWeekStart(row.date)
        : row.date.substring(0, 7)
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        incidentsOpened: 0,
        incidentsClosed: 0,
        incidentsAutoClosed: 0,
        alarmsSent: 0,
        mttaSum: 0,
        mttaCount: 0,
        mttrSum: 0,
        mttrCount: 0
      })
    }
    const g = groups.get(key)
    g.incidentsOpened += row.incidentsOpened
    g.incidentsClosed += row.incidentsClosed
    g.incidentsAutoClosed += row.incidentsAutoClosed
    g.alarmsSent += row.alarmsSent
    g.mttaSum += row.mttaSum
    g.mttaCount += row.mttaCount
    g.mttrSum += row.mttrSum
    g.mttrCount += row.mttrCount
  }

  return [...groups.values()].map(g => ({
    label:
      aggregation === 'weekly'
        ? formatWeekLabel(g.key)
        : formatMonthLabel(g.key),
    ...g
  }))
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatCard = ({ label, value, sub }) => (
  <Card
    background="textBackgroundColor"
    width="small"
    flex={{ shrink: 0 }}
    elevation="small">
    <CardBody pad="small" gap="xsmall">
      <Text size="small" color="darkTint">
        {label}
      </Text>
      <Text size="xlarge" weight="bold">
        {value ?? '—'}
      </Text>
      {sub && (
        <Text size="xsmall" color="darkTint">
          {sub}
        </Text>
      )}
    </CardBody>
  </Card>
)

const SectionHeading = ({ label }) => (
  <Heading
    level={5}
    margin={{ top: 'medium', bottom: 'xsmall' }}
    color="darkTint"
    style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
    {label}
  </Heading>
)

const SegmentedButtons = ({ options, value, onChange }) => (
  <Box direction="row" gap="xsmall">
    {options.map(opt => (
      <Button
        key={opt.value}
        label={opt.label}
        size="small"
        primary={value === opt.value}
        onClick={() => onChange(opt.value)}
      />
    ))}
  </Box>
)

const AGGREGATION_OPTIONS = [
  { labelKey: 'daily', value: 'daily' },
  { labelKey: 'weekly', value: 'weekly' },
  { labelKey: 'monthly', value: 'monthly' }
]

const AggregationDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef()
  const selected = AGGREGATION_OPTIONS.find(o => o.value === value)

  return (
    <>
      <Box
        ref={triggerRef}
        direction="row"
        align="center"
        gap="xsmall"
        pad={{ vertical: '4px', horizontal: '10px' }}
        border={{ color: 'border' }}
        round="4px"
        background="background"
        onClick={() => setOpen(o => !o)}
        style={{ cursor: 'pointer', userSelect: 'none' }}>
        <Text size="small">{I18n.t(selected?.labelKey ?? 'daily')}</Text>
        <FormDown size="small" />
      </Box>
      {open && triggerRef.current && (
        <Drop
          target={triggerRef.current}
          onClickOutside={() => setOpen(false)}
          onEsc={() => setOpen(false)}
          align={{ top: 'bottom', right: 'right' }}>
          <Box pad="xsmall" background="background">
            {AGGREGATION_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                label={I18n.t(opt.labelKey)}
                size="small"
                plain
                active={opt.value === value}
                style={{
                  textAlign: 'left',
                  paddingBlock: '6px',
                  paddingInline: '12px'
                }}
                onClick={() => {
                  onChange(opt.value)
                  setOpen(false)
                }}
              />
            ))}
          </Box>
        </Drop>
      )}
    </>
  )
}

const DateRangePicker = ({ startDate, endDate, onChange }) => {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef()

  const handleShortcut = days => {
    onChange(daysAgoStr(days), todayStr())
    setOpen(false)
  }

  const handleDateInputChange = ({ value }) => {
    if (Array.isArray(value) && value.length === 2 && value[0] && value[1]) {
      onChange(value[0].split('T')[0], value[1].split('T')[0])
      setOpen(false)
    }
  }

  const rangeLabel = `${formatDayLabel(startDate)} – ${formatDayLabel(endDate)}`

  return (
    <>
      <Box
        ref={triggerRef}
        direction="row"
        align="center"
        gap="xsmall"
        pad={{ vertical: '4px', horizontal: '10px' }}
        border={{ color: 'border' }}
        round="4px"
        background="background"
        onClick={() => setOpen(o => !o)}
        style={{ cursor: 'pointer', userSelect: 'none' }}>
        <Text size="small">{rangeLabel}</Text>
        <FormDown size="small" />
      </Box>
      {open && triggerRef.current && (
        <Drop
          target={triggerRef.current}
          onClickOutside={() => setOpen(false)}
          onEsc={() => setOpen(false)}
          align={{ top: 'bottom', right: 'right' }}>
          <Box direction="row" background="background">
            <Box
              pad="small"
              gap="xxsmall"
              border={{ side: 'right', color: 'light-4' }}
              width="small"
              flex={{ shrink: 0 }}>
              {SHORTCUT_OPTIONS.map(opt => (
                <Button
                  key={opt.days}
                  label={I18n.t(opt.labelKey)}
                  size="small"
                  plain
                  style={{ textAlign: 'left', paddingBlock: '6px' }}
                  onClick={() => handleShortcut(opt.days)}
                />
              ))}
            </Box>
            <Box pad="xsmall">
              <DateInput
                inline
                size="small"
                value={[startDate, endDate]}
                onChange={handleDateInputChange}
              />
            </Box>
          </Box>
        </Drop>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const UsageAnalytics = () => {
  const enterpriseAccountId = window.localStorage.getItem('enterpriseAccountId')

  const memberCount = useSelector(state => state.accountInfo.members.length)
  const teamCount = useSelector(state => state.accountInfo.teams.length)
  const rotationCount = useSelector(state => state.accountInfo.rotations.length)
  const listenerCount = useSelector(state => state.accountInfo.alerts.length)

  const [current, setCurrent] = useState(null)
  const [dailyRows, setDailyRows] = useState([]) // sorted ascending by date
  const [loading, setLoading] = useState(true)

  // UI state
  const [startDate, setStartDate] = useState(() => daysAgoStr(30))
  const [endDate, setEndDate] = useState(() => todayStr())
  const [incidentView, setIncidentView] = useState('numbers') // 'numbers' | 'chart'
  const [aggregation, setAggregation] = useState('daily') // 'daily' | 'weekly' | 'monthly'

  const handleDateRangeChange = (start, end) => {
    setStartDate(start)
    setEndDate(end)
  }

  // Subscribe to live current counters
  useEffect(() => {
    if (!enterpriseAccountId) return

    const currentDocRef = doc(
      GlobalConfig.firestoreDb,
      'enterpriseAnalytics',
      enterpriseAccountId,
      'current',
      'data'
    )

    const unsubscribe = onSnapshot(currentDocRef, snap => {
      setCurrent(snap.exists() ? snap.data() : null)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [enterpriseAccountId])

  // Fetch all available daily snapshots, ascending
  useEffect(() => {
    if (!enterpriseAccountId) return

    const dailyRef = collection(
      GlobalConfig.firestoreDb,
      'enterpriseAnalytics',
      enterpriseAccountId,
      'daily'
    )

    getDocs(query(dailyRef, orderBy('date', 'asc')))
      .then(snap => {
        const rows = snap.docs.map(d => ({
          date: d.id,
          incidentsOpened: d.data().incidentsOpened ?? 0,
          incidentsClosed: d.data().incidentsClosed ?? 0,
          incidentsOpen: d.data().incidentsOpen ?? 0,
          incidentsAcknowledged: d.data().incidentsAcknowledged ?? 0,
          incidentsAutoClosed: d.data().incidentsAutoClosed ?? 0,
          alarmsSent: d.data().alarmsSent ?? 0,
          mttaSum: d.data().mttaSum ?? 0,
          mttaCount: d.data().mttaCount ?? 0,
          mttrSum: d.data().mttrSum ?? 0,
          mttrCount: d.data().mttrCount ?? 0
        }))
        setDailyRows(rows)
      })
      .catch(err => console.error('Error fetching daily analytics:', err))
  }, [enterpriseAccountId])

  // ---------- Compute period-scoped data from daily snapshots ----------

  const periodData = useMemo(() => {
    if (dailyRows.length === 0) return null

    const periodStartIdx = dailyRows.findIndex(r => r.date >= startDate)
    if (periodStartIdx === -1) return null

    // Find last row on or before endDate
    let periodEndIdx = dailyRows.length - 1
    for (let i = dailyRows.length - 1; i >= 0; i--) {
      if (dailyRows[i].date <= endDate) {
        periodEndIdx = i
        break
      }
    }
    if (periodEndIdx < periodStartIdx) return null

    // Include one prior row as context to compute the first day's increment
    const contextIdx = Math.max(0, periodStartIdx - 1)
    const contextRows = dailyRows.slice(contextIdx, periodEndIdx + 1)
    const increments = computeIncrements(contextRows)

    const totalOpened = increments.reduce((s, r) => s + r.incidentsOpened, 0)
    const totalClosed = increments.reduce((s, r) => s + r.incidentsClosed, 0)
    const totalAutoClosed = increments.reduce(
      (s, r) => s + r.incidentsAutoClosed,
      0
    )
    const totalAlarmsSent = increments.reduce((s, r) => s + r.alarmsSent, 0)
    const totalMttaSum = increments.reduce((s, r) => s + r.mttaSum, 0)
    const totalMttaCount = increments.reduce((s, r) => s + r.mttaCount, 0)
    const totalMttrSum = increments.reduce((s, r) => s + r.mttrSum, 0)
    const totalMttrCount = increments.reduce((s, r) => s + r.mttrCount, 0)

    // Point-in-time values from the last snapshot in the period
    const latest = dailyRows[periodEndIdx]

    return {
      incidentsOpened: totalOpened,
      incidentsClosed: totalClosed,
      incidentsAutoClosed: totalAutoClosed,
      incidentsOpen: latest.incidentsOpen,
      incidentsAcknowledged: latest.incidentsAcknowledged,
      alarmsSent: totalAlarmsSent,
      mtta: avg(totalMttaSum, totalMttaCount),
      mttr: avg(totalMttrSum, totalMttrCount),
      increments
    }
  }, [dailyRows, startDate, endDate])

  // ---------- Chart data ----------

  const chartRows = useMemo(() => {
    if (!periodData) return []
    return aggregateIncrements(periodData.increments, aggregation)
  }, [periodData, aggregation])

  const chartData = useMemo(
    () => ({
      labels: chartRows.map(r => r.label),
      datasets: [
        {
          label: I18n.t('incidentsOpened'),
          data: chartRows.map(r => r.incidentsOpened),
          backgroundColor: 'rgba(54, 162, 235, 0.7)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        },
        {
          label: I18n.t('incidentsClosed'),
          data: chartRows.map(r => r.incidentsClosed),
          backgroundColor: 'rgba(75, 192, 192, 0.7)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    }),
    [chartRows]
  )

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { position: 'top' },
        title: { display: false }
      },
      scales: {
        y: { beginAtZero: true, ticks: { precision: 0 } }
      }
    }),
    []
  )

  // ---------- Resolved display values ----------

  const mttaMs =
    typeof periodData?.mtta === 'number'
      ? periodData.mtta
      : current
      ? avg(current.mttaSum, current.mttaCount)
      : null

  const mttrMs =
    typeof periodData?.mttr === 'number'
      ? periodData.mttr
      : current
      ? avg(current.mttrSum, current.mttrCount)
      : null

  const durationLabel = `${formatDayLabel(startDate)} – ${formatDayLabel(
    endDate
  )}`

  const hasData = current !== null

  if (loading) {
    return (
      <Box fill align="center" justify="center">
        <Spinner size="medium" />
      </Box>
    )
  }

  return (
    <Box pad="small" overflow="auto">
      {/* ── Header + duration picker ── */}
      <Box
        direction="row"
        align="center"
        justify="between"
        margin="small"
        wrap
        gap="small"
        flex={{ shrink: 0 }}>
        <Heading level={3} margin="none">
          {I18n.t('usageAnalytics')}
        </Heading>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateRangeChange}
        />
      </Box>

      <Box margin={{ left: 'small' }}>
        {/* ── Incidents ── */}
        <Box
          direction="row"
          align="center"
          justify="between"
          margin={{ top: 'medium', bottom: 'xsmall' }}
          wrap
          gap="small"
          flex={{ shrink: 0 }}>
          <Heading
            level={5}
            margin="none"
            color="darkTint"
            style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Incidents
          </Heading>
          <Box direction="row" gap="none" align="center" flex={{ shrink: 0 }}>
            <SegmentedButtons
              options={[
                { label: I18n.t('numbersView'), value: 'numbers' },
                { label: I18n.t('chartView'), value: 'chart' }
              ]}
              value={incidentView}
              onChange={setIncidentView}
            />
            {incidentView === 'chart' && (
              <>
                <Box pad={{ horizontal: 'small' }}>
                  <Box width="2px" height="20px" background="light-4" />
                </Box>
                <AggregationDropdown
                  value={aggregation}
                  onChange={setAggregation}
                />
              </>
            )}
          </Box>
        </Box>

        {!hasData ? (
          <Text color="darkTint" size="small">
            {I18n.t('noAnalyticsData')}
          </Text>
        ) : incidentView === 'numbers' ? (
          <Box direction="row" wrap gap="small">
            <StatCard
              label={I18n.t('incidentsOpened')}
              value={
                periodData?.incidentsOpened ?? current?.incidentsOpened ?? 0
              }
              sub={durationLabel}
            />
            <StatCard
              label={I18n.t('incidentsOpen')}
              value={periodData?.incidentsOpen ?? current?.incidentsOpen ?? 0}
            />
            <StatCard
              label={I18n.t('incidentsAcknowledged')}
              value={
                periodData?.incidentsAcknowledged ??
                current?.incidentsAcknowledged ??
                0
              }
            />
            <StatCard
              label={I18n.t('incidentsClosed')}
              value={
                periodData?.incidentsClosed ?? current?.incidentsClosed ?? 0
              }
              sub={
                (periodData?.incidentsAutoClosed ||
                  current?.incidentsAutoClosed) &&
                `${
                  periodData?.incidentsAutoClosed ??
                  current?.incidentsAutoClosed
                } auto-closed`
              }
            />
          </Box>
        ) : (
          <Box
            background="textBackgroundColor"
            round="small"
            elevation="small"
            pad="medium"
            alignSelf={chartRows.length === 0 ? 'start' : 'stretch'}
            flex={{ shrink: 0 }}>
            {chartRows.length === 0 ? (
              <Text color="darkTint" size="small">
                {I18n.t('noAnalyticsData')}
              </Text>
            ) : (
              <Bar data={chartData} options={chartOptions} />
            )}
          </Box>
        )}

        {/* ── Response Times ── */}
        <SectionHeading label="Response Times" />
        {!hasData ? (
          <Text color="darkTint" size="small">
            {I18n.t('noAnalyticsData')}
          </Text>
        ) : (
          <Box direction="row" wrap gap="small">
            <StatCard
              label={I18n.t('avgTimeToAcknowledge')}
              value={mttaMs !== null ? formatDuration(mttaMs) : '—'}
              sub={mttaMs !== null ? durationLabel : 'no data yet'}
            />
            <StatCard
              label={I18n.t('avgTimeToResolve')}
              value={mttrMs !== null ? formatDuration(mttrMs) : '—'}
              sub={mttrMs !== null ? durationLabel : 'no data yet'}
            />
            <StatCard
              label={I18n.t('alarmsSent')}
              value={periodData?.alarmsSent ?? current?.alarmsSent ?? 0}
              sub={durationLabel}
            />
          </Box>
        )}

        {/* ── Configuration ── */}
        <SectionHeading label="Configuration" />
        <Box direction="row" wrap gap="small">
          <StatCard label={I18n.t('members')} value={memberCount} />
          <StatCard label={I18n.t('teams')} value={teamCount} />
          <StatCard label={I18n.t('rotations')} value={rotationCount} />
          <StatCard label={I18n.t('listeners')} value={listenerCount} />
        </Box>
      </Box>
    </Box>
  )
}

export default UsageAnalytics
